import type { GuidedAnswer, GuidedModeDefinition, GuidedModeStep } from '../types/guided-mode.types'

export type CalculatedDateStepDependencies = ReadonlyMap<string, readonly string[]>

export function calculateDate(value: GuidedAnswer, addDays: number | undefined): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value) || !Number.isInteger(addDays)) return undefined
  const day = value.slice(0, 10)
  const date = new Date(`${day}T00:00:00.000Z`)
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== day) return undefined
  date.setUTCDate(date.getUTCDate() + addDays!)
  return Number.isFinite(date.getTime()) ? date.toISOString() : undefined
}

export function calculatedDateAnswers(definition: GuidedModeDefinition | null, answers: Record<string, GuidedAnswer>) {
  const values: Record<string, GuidedAnswer> = {}
  const visit = (steps: GuidedModeStep[]) => {
    for (const step of steps) {
      if (step.steps) visit(step.steps)
      if (step.collection?.type !== 'calculated_date') continue
      const question = definition?.questions[step.questionId || '']
      if (!question) continue
      values[question.conceptId] = calculateDate(answers[step.collection.sourceConceptId || ''], step.collection.addDays)
    }
  }
  visit(definition?.assessment?.steps || [])
  return values
}

export function buildCalculatedDateStepDependencies(
  definition: GuidedModeDefinition | null,
): CalculatedDateStepDependencies {
  const stepEntries: Array<{ groupId?: string; step: GuidedModeStep; stepId: string }> = []
  const visit = (steps: GuidedModeStep[], groupId?: string) => {
    for (const step of steps) {
      if (step.steps) {
        visit(step.steps, step.id || groupId)
      } else if (step.id) {
        stepEntries.push({ groupId, step, stepId: step.id })
      }
    }
  }
  visit(definition?.assessment?.steps || [])

  const sourceStepIdByConceptId = new Map<string, string>()
  for (const { step, stepId } of stepEntries) {
    const conceptId = step.questionId ? definition?.questions[step.questionId]?.conceptId : undefined
    if (conceptId) sourceStepIdByConceptId.set(conceptId, stepId)
  }

  const groupIdByStepId = new Map(stepEntries.map(({ groupId, stepId }) => [stepId, groupId]))
  const dependencies = new Map<string, string[]>()
  for (const { groupId, step, stepId } of stepEntries) {
    if (step.collection?.type !== 'calculated_date' || !step.collection.sourceConceptId) continue
    const sourceStepId = sourceStepIdByConceptId.get(step.collection.sourceConceptId)
    if (!sourceStepId || groupIdByStepId.get(sourceStepId) !== groupId) continue
    const dependentStepIds = dependencies.get(sourceStepId) || []
    dependencies.set(sourceStepId, [...dependentStepIds, stepId])
  }
  return dependencies
}

export function includeCalculatedDateDependents(
  stepIds: readonly string[],
  dependencies: CalculatedDateStepDependencies,
): string[] {
  const expanded: string[] = []
  const added = new Set<string>()
  for (const stepId of stepIds) {
    if (!added.has(stepId)) {
      expanded.push(stepId)
      added.add(stepId)
    }
    for (const dependentStepId of dependencies.get(stepId) || []) {
      if (added.has(dependentStepId)) continue
      expanded.push(dependentStepId)
      added.add(dependentStepId)
    }
  }
  return expanded
}
